import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  gql,
  useApolloClient,
  useLazyQuery,
  useMutation,
  useQuery,
} from "@apollo/client";
import "./SubscriptionsTab.css";

// Super-admin only. Surfaces Stripe subscription totals + a list of
// active subs + per-row actions: credit free months, pause (optionally
// until a date), unpause, cancel (at period end or immediately).
//
// All Stripe writes go through admin-only graphql mutations; the
// Lambda's STRIPE_SECRET_KEY env var has to be populated for any of
// this to do real work. When the key isn't set, every query / mutation
// returns a "Stripe is not configured" error and the tab shows that
// message in the banner rather than crashing.

const SUMMARY_QUERY = gql`
  query SubscriptionsSummary {
    subscriptionsSummary {
      active
      trialing
      paused
      cancelled
      pastDue
      unpaid
      currency
      monthlyActiveRevenue
      totalCreditBalance
      expectedNetMonthly
    }
  }
`;

const LIST_QUERY = gql`
  query SubscriptionsList($status: String) {
    subscriptionsList(status: $status) {
      id
      customerId
      customerEmail
      customerName
      status
      currentPeriodEnd
      cancelAtPeriodEnd
      pausedUntil
      pauseBehavior
      planNickname
      amount
      currency
      intervalUnit
      creditBalance
    }
  }
`;

const FIND_BY_EMAIL = gql`
  query FindStripeCustomerByEmail($email: String!) {
    findStripeCustomerByEmail(email: $email) {
      id
      customerId
      customerEmail
      customerName
      status
      currentPeriodEnd
      cancelAtPeriodEnd
      pausedUntil
      pauseBehavior
      planNickname
      amount
      currency
      intervalUnit
      creditBalance
    }
  }
`;

const CREDIT_MONTHS = gql`
  mutation CreditCaptainMonths(
    $customerId: ID!
    $months: Int!
    $description: String
  ) {
    creditCaptainMonths(
      customerId: $customerId
      months: $months
      description: $description
    ) {
      id
      creditBalance
    }
  }
`;

const CAPTAIN_INCENTIVES = gql`
  query CaptainIncentives($customerId: ID!) {
    captainIncentives(customerId: $customerId) {
      id
      source
      createdAt
      type
      description
      amount
      currency
      endingBalance
    }
  }
`;

const PAUSE_SUB = gql`
  mutation PauseStripeSubscription($subscriptionId: ID!, $pausedUntil: String) {
    pauseStripeSubscription(
      subscriptionId: $subscriptionId
      pausedUntil: $pausedUntil
    ) {
      id
      status
      pausedUntil
      pauseBehavior
    }
  }
`;

const UNPAUSE_SUB = gql`
  mutation UnpauseStripeSubscription($subscriptionId: ID!) {
    unpauseStripeSubscription(subscriptionId: $subscriptionId) {
      id
      status
      pausedUntil
    }
  }
`;

const CANCEL_SUB = gql`
  mutation CancelStripeSubscription($subscriptionId: ID!, $immediately: Boolean) {
    cancelStripeSubscription(
      subscriptionId: $subscriptionId
      immediately: $immediately
    ) {
      id
      status
      cancelAtPeriodEnd
    }
  }
`;

const STATUS_OPTIONS = [
  { id: "active", label: "Active" },
  { id: "trialing", label: "Trial" },
  { id: "paused", label: "Paused" },
  { id: "past_due", label: "Past due" },
  { id: "unpaid", label: "Unpaid" },
  { id: "canceled", label: "Canceled" },
  { id: "all", label: "All" },
];

const formatMoney = (cents, currency) => {
  if (typeof cents !== "number") return "—";
  try {
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: (currency || "usd").toUpperCase(),
    }).format(cents / 100);
  } catch {
    return `$${(cents / 100).toFixed(2)}`;
  }
};

const formatDate = (iso) => {
  if (!iso) return "—";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleDateString();
};

const SubscriptionsTab = () => {
  const [statusFilter, setStatusFilter] = useState("active");
  const [search, setSearch] = useState("");
  const [searchHits, setSearchHits] = useState(null); // null = no search yet

  const summaryQ = useQuery(SUMMARY_QUERY, { fetchPolicy: "network-only" });
  const listQ = useQuery(LIST_QUERY, {
    variables: { status: statusFilter },
    fetchPolicy: "network-only",
  });
  const [doSearch, searchQ] = useLazyQuery(FIND_BY_EMAIL, {
    fetchPolicy: "network-only",
    onCompleted: (data) => setSearchHits(data?.findStripeCustomerByEmail || []),
  });

  const [creditMonths] = useMutation(CREDIT_MONTHS);
  const [pauseSub] = useMutation(PAUSE_SUB);
  const [unpauseSub] = useMutation(UNPAUSE_SUB);
  const [cancelSub] = useMutation(CANCEL_SUB);

  // History expander state. Key = subscription id (one expander per
  // row). Value = { loading, error, entries } so multiple rows can be
  // open at once without contending for a single useQuery. We use
  // Apollo's imperative client.query so each refresh hits the network
  // fresh after a credit/cancel.
  const apollo = useApolloClient();
  const [historyState, setHistoryState] = useState({});

  const loadHistory = useCallback(
    async (sub) => {
      setHistoryState((s) => ({
        ...s,
        [sub.id]: { loading: true, error: null, entries: null, customerId: sub.customerId },
      }));
      try {
        const result = await apollo.query({
          query: CAPTAIN_INCENTIVES,
          variables: { customerId: sub.customerId },
          fetchPolicy: "network-only",
        });
        setHistoryState((s) => ({
          ...s,
          [sub.id]: {
            loading: false,
            error: null,
            entries: result.data?.captainIncentives || [],
            customerId: sub.customerId,
          },
        }));
      } catch (err) {
        setHistoryState((s) => ({
          ...s,
          [sub.id]: {
            loading: false,
            error: err.message || String(err),
            entries: null,
            customerId: sub.customerId,
          },
        }));
      }
    },
    [apollo]
  );

  const toggleHistory = useCallback(
    (sub) => {
      setHistoryState((s) => {
        const existing = s[sub.id];
        if (existing && existing.entries !== null) {
          // Already loaded — toggle by removing.
          const next = { ...s };
          delete next[sub.id];
          return next;
        }
        return s;
      });
      const cur = historyState[sub.id];
      if (!cur || cur.entries === null) {
        loadHistory(sub);
      }
    },
    [historyState, loadHistory]
  );

  const refetchAll = useCallback(() => {
    summaryQ.refetch();
    listQ.refetch();
    if (searchHits !== null && search.trim()) {
      doSearch({ variables: { email: search.trim().toLowerCase() } });
    }
  }, [summaryQ, listQ, searchHits, search, doSearch]);

  const handleSearch = (e) => {
    e.preventDefault();
    const q = search.trim().toLowerCase();
    if (!q) {
      setSearchHits(null);
      return;
    }
    doSearch({ variables: { email: q } });
  };

  const handleCredit = async (sub) => {
    const raw = window.prompt(
      `Credit how many months to ${sub.customerEmail || sub.customerId}?\n` +
        `Plan: ${formatMoney(sub.amount, sub.currency)} / ${sub.intervalUnit}.\n` +
        `Positive = credit; negative = remove credit.`,
      "1"
    );
    if (raw === null) return;
    const months = parseInt(raw, 10);
    if (!Number.isFinite(months) || months === 0) {
      window.alert("Enter a non-zero integer.");
      return;
    }
    // Second prompt captures the reason. Stored verbatim on the Stripe
    // balance transaction so the F.2 history view shows context.
    const description = window.prompt(
      "Reason / note (optional — e.g. 'for hosting May volleyball'):",
      ""
    );
    if (description === null) return; // explicit cancel
    try {
      await creditMonths({
        variables: {
          customerId: sub.customerId,
          months,
          description: description.trim() || null,
        },
      });
      refetchAll();
      // If history is open on this row, refresh it too so the new
      // entry shows up immediately.
      if (historyState[sub.id]) loadHistory(sub);
      window.alert(`Credited ${months} month${Math.abs(months) === 1 ? "" : "s"}.`);
    } catch (err) {
      window.alert(`Credit failed: ${err.message || err}`);
    }
  };

  const handlePause = async (sub) => {
    const raw = window.prompt(
      "Pause until when? Enter a date (YYYY-MM-DD) or leave blank to pause indefinitely.",
      ""
    );
    if (raw === null) return;
    const trimmed = raw.trim();
    let pausedUntil = null;
    if (trimmed) {
      if (!/^\d{4}-\d{2}-\d{2}$/.test(trimmed)) {
        window.alert("Date must be YYYY-MM-DD.");
        return;
      }
      pausedUntil = `${trimmed}T00:00:00Z`;
    }
    try {
      await pauseSub({
        variables: { subscriptionId: sub.id, pausedUntil },
      });
      refetchAll();
    } catch (err) {
      window.alert(`Pause failed: ${err.message || err}`);
    }
  };

  const handleUnpause = async (sub) => {
    if (!window.confirm(`Resume ${sub.customerEmail || sub.customerId}'s subscription?`)) {
      return;
    }
    try {
      await unpauseSub({ variables: { subscriptionId: sub.id } });
      refetchAll();
    } catch (err) {
      window.alert(`Unpause failed: ${err.message || err}`);
    }
  };

  const handleCancel = async (sub, immediately) => {
    const verb = immediately ? "cancel immediately (no refund)" : "cancel at period end";
    if (
      !window.confirm(
        `Confirm: ${verb} for ${sub.customerEmail || sub.customerId}?`
      )
    ) {
      return;
    }
    try {
      await cancelSub({
        variables: { subscriptionId: sub.id, immediately: !!immediately },
      });
      refetchAll();
    } catch (err) {
      window.alert(`Cancel failed: ${err.message || err}`);
    }
  };

  const visibleList = useMemo(() => {
    if (searchHits !== null) return searchHits;
    return (listQ.data && listQ.data.subscriptionsList) || [];
  }, [searchHits, listQ.data]);

  const summary = summaryQ.data && summaryQ.data.subscriptionsSummary;
  const currency = summary?.currency || "usd";

  const renderRow = (sub) => {
    const statusClass = `subscriptions-tab__status subscriptions-tab__status--${(sub.status || "").toLowerCase()}`;
    const isPaused = sub.status === "paused" || sub.pauseBehavior;
    return (
      <div key={sub.id} className="subscriptions-tab__row">
        <div className="subscriptions-tab__row-head">
          <div>
            <div className="subscriptions-tab__row-name">
              {sub.customerName || sub.customerEmail || sub.customerId}
            </div>
            <div className="subscriptions-tab__row-meta">
              <span>{sub.customerEmail || "(no email)"}</span>
              <span className={statusClass}>{sub.status}</span>
              {sub.cancelAtPeriodEnd && (
                <span className="subscriptions-tab__status subscriptions-tab__status--canceled">
                  cancels at period end
                </span>
              )}
              {sub.pausedUntil && (
                <span>· paused until {formatDate(sub.pausedUntil)}</span>
              )}
            </div>
          </div>
          <div className="subscriptions-tab__row-money">
            <div>
              {formatMoney(sub.amount, sub.currency)}
              {sub.intervalUnit && ` / ${sub.intervalUnit}`}
            </div>
            <div className="subscriptions-tab__renews">
              renews {formatDate(sub.currentPeriodEnd)}
            </div>
            {!!sub.creditBalance && (
              <div className="subscriptions-tab__credit">
                {formatMoney(sub.creditBalance, sub.currency)} credit
              </div>
            )}
          </div>
        </div>
        <div className="subscriptions-tab__actions">
          <button
            type="button"
            className="subscriptions-tab__btn"
            onClick={() => handleCredit(sub)}
          >
            Credit months
          </button>
          {isPaused ? (
            <button
              type="button"
              className="subscriptions-tab__btn"
              onClick={() => handleUnpause(sub)}
            >
              Resume
            </button>
          ) : (
            <button
              type="button"
              className="subscriptions-tab__btn"
              onClick={() => handlePause(sub)}
            >
              Pause…
            </button>
          )}
          <button
            type="button"
            className="subscriptions-tab__btn"
            onClick={() => handleCancel(sub, false)}
            disabled={sub.cancelAtPeriodEnd || sub.status === "canceled"}
          >
            Cancel @ period end
          </button>
          <button
            type="button"
            className="subscriptions-tab__btn subscriptions-tab__btn--danger"
            onClick={() => handleCancel(sub, true)}
            disabled={sub.status === "canceled"}
          >
            Cancel now
          </button>
          <button
            type="button"
            className="subscriptions-tab__btn"
            onClick={() => toggleHistory(sub)}
          >
            {historyState[sub.id] ? "Hide history" : "Show history"}
          </button>
        </div>
        {historyState[sub.id] && (
          <div className="subscriptions-tab__history">
            {historyState[sub.id].loading && (
              <div className="subscriptions-tab__info">Loading history…</div>
            )}
            {historyState[sub.id].error && (
              <div className="subscriptions-tab__error">
                {historyState[sub.id].error}
              </div>
            )}
            {historyState[sub.id].entries &&
              historyState[sub.id].entries.length === 0 && (
                <div className="subscriptions-tab__info">
                  No incentive history yet.
                </div>
              )}
            {historyState[sub.id].entries &&
              historyState[sub.id].entries.length > 0 && (
                <table className="subscriptions-tab__history-table">
                  <thead>
                    <tr>
                      <th>When</th>
                      <th>Amount</th>
                      <th>Description</th>
                    </tr>
                  </thead>
                  <tbody>
                    {historyState[sub.id].entries.map((entry) => {
                      // Stripe customer balance: negative amount = credit
                      // applied to future invoices. Surface as a positive
                      // "+$X credit" so the human reads "I gave them money."
                      const isCredit =
                        typeof entry.amount === "number" && entry.amount < 0;
                      const isDebit =
                        typeof entry.amount === "number" && entry.amount > 0;
                      const absMoney = formatMoney(
                        Math.abs(entry.amount || 0),
                        entry.currency
                      );
                      return (
                        <tr key={entry.id}>
                          <td>{formatDate(entry.createdAt)}</td>
                          <td
                            className={
                              isCredit
                                ? "subscriptions-tab__history-credit"
                                : isDebit
                                ? "subscriptions-tab__history-debit"
                                : ""
                            }
                          >
                            {isCredit ? `+${absMoney}` : isDebit ? `−${absMoney}` : "—"}
                          </td>
                          <td>{entry.description || entry.type || "—"}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              )}
          </div>
        )}
      </div>
    );
  };

  return (
    <div className="subscriptions-tab">
      <h2 className="subscriptions-tab__title">Subscriptions</h2>

      <div className="subscriptions-tab__tiles">
        <div className="subscriptions-tab__tile">
          <div className="subscriptions-tab__tile-label">Active</div>
          <div className="subscriptions-tab__tile-value">
            {summary ? summary.active : "—"}
          </div>
        </div>
        <div className="subscriptions-tab__tile">
          <div className="subscriptions-tab__tile-label">Trial</div>
          <div className="subscriptions-tab__tile-value">
            {summary ? summary.trialing : "—"}
          </div>
        </div>
        <div className="subscriptions-tab__tile">
          <div className="subscriptions-tab__tile-label">Paused</div>
          <div className="subscriptions-tab__tile-value">
            {summary ? summary.paused : "—"}
          </div>
        </div>
        <div className="subscriptions-tab__tile">
          <div className="subscriptions-tab__tile-label">Past due</div>
          <div className="subscriptions-tab__tile-value">
            {summary ? summary.pastDue : "—"}
          </div>
        </div>
        <div className="subscriptions-tab__tile">
          <div className="subscriptions-tab__tile-label">Unpaid</div>
          <div className="subscriptions-tab__tile-value">
            {summary ? summary.unpaid : "—"}
          </div>
        </div>
        <div className="subscriptions-tab__tile">
          <div className="subscriptions-tab__tile-label">Canceled</div>
          <div className="subscriptions-tab__tile-value">
            {summary ? summary.cancelled : "—"}
          </div>
        </div>
        <div className="subscriptions-tab__tile subscriptions-tab__tile--wide">
          <div className="subscriptions-tab__tile-label">
            Monthly recurring (active + trial)
          </div>
          <div className="subscriptions-tab__tile-value">
            {summary
              ? formatMoney(summary.monthlyActiveRevenue, currency)
              : "—"}
          </div>
        </div>
        <div className="subscriptions-tab__tile">
          <div className="subscriptions-tab__tile-label">Credit balance</div>
          <div className="subscriptions-tab__tile-value">
            {summary
              ? formatMoney(summary.totalCreditBalance, currency)
              : "—"}
          </div>
        </div>
        <div className="subscriptions-tab__tile subscriptions-tab__tile--accent">
          <div className="subscriptions-tab__tile-label">
            Expected net this month
          </div>
          <div className="subscriptions-tab__tile-value">
            {summary
              ? formatMoney(summary.expectedNetMonthly, currency)
              : "—"}
          </div>
        </div>
      </div>

      {summaryQ.error && (
        <div className="subscriptions-tab__error">
          Error loading summary: {summaryQ.error.message}
        </div>
      )}

      <form className="subscriptions-tab__search" onSubmit={handleSearch}>
        <input
          type="search"
          placeholder="Find by email…"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
        <button type="submit" className="subscriptions-tab__btn">
          Search
        </button>
        {searchHits !== null && (
          <button
            type="button"
            className="subscriptions-tab__btn"
            onClick={() => {
              setSearchHits(null);
              setSearch("");
            }}
          >
            Clear
          </button>
        )}
      </form>

      {searchHits === null && (
        <div className="subscriptions-tab__filters">
          {STATUS_OPTIONS.map((o) => (
            <button
              key={o.id}
              type="button"
              className={
                statusFilter === o.id
                  ? "subscriptions-tab__chip subscriptions-tab__chip--active"
                  : "subscriptions-tab__chip"
              }
              onClick={() => setStatusFilter(o.id)}
            >
              {o.label}
            </button>
          ))}
          <button
            type="button"
            className="subscriptions-tab__btn"
            onClick={refetchAll}
            disabled={listQ.loading || summaryQ.loading}
          >
            Refresh
          </button>
        </div>
      )}

      {(listQ.loading || searchQ.loading) && (
        <div className="subscriptions-tab__info">Loading…</div>
      )}
      {listQ.error && (
        <div className="subscriptions-tab__error">
          Error loading list: {listQ.error.message}
        </div>
      )}
      {searchQ.error && (
        <div className="subscriptions-tab__error">
          Search failed: {searchQ.error.message}
        </div>
      )}

      {!listQ.loading && !searchQ.loading && visibleList.length === 0 && (
        <div className="subscriptions-tab__info">
          {searchHits !== null
            ? "No Stripe subscriptions for that email."
            : "No subscriptions in this status."}
        </div>
      )}

      <div className="subscriptions-tab__list">{visibleList.map(renderRow)}</div>
    </div>
  );
};

export default SubscriptionsTab;
